import { i18n } from 'fishx';
import { message, Modal } from '@whalecloud/fdx';
import lrz from 'lrz';
import get from 'lodash/get';
import { getRequestPrefix, getBusinessI18n, loginSession, successAlert, failAlert, defaultCurrencyUnit } from '@/utils';

/**
 * 获取接口返回data数据
 */
export function getRequestData(response = {}, showMessage = true) {
  const { success, data = {} } = response;
  if (!success || String(data.code) !== '200') {
    // 请求出错
    if (showMessage && data.message) {
      message.error(data.message);
    }
    return {};
  }
  return data.data || {};
}

/**
 * 获取tt接口返回data数据
 */
export function getTTRequestData(response = {}, showMessage = true) {
  const { success, errorMsg, data = {} } = response;
  if (!success) {
    // 请求出错
    if (showMessage && errorMsg) {
      message.error(errorMsg);
    }
    return {};
  }
  return data || {};
}

// 固网业务，包括新开、二次业务，接口调用后 失败 or 成功 的操作
export function isFixedServiceSuccess(result, successFun) {
  const { returnCode, custOrderNbr, returnMsg } = result || {};
  if (`${returnCode}` === '0' && custOrderNbr) {
    // 成功弹窗
    successAlert({
      content: i18n('ORDER_SUBMITTED_SUCCESSFULLY', { custOrderNbr }),
      callbackFunc: successFun,
    });
  } else {
    failAlert({
      content: returnMsg,
    });
  }
}

export const showTotal = (total, range) => `${i18n('PAGINATION_VIEW')} ${range[0]} - ${range[1]}/${total}`;

// 判断是否是ReactDmo
export function isClassComponent(component) {
  return typeof component === 'function' && !!component.prototype.isReactComponent;
}

export function isFunctionComponent(component) {
  return typeof component === 'function' && String(component).includes('default.a.createElement');
}

export function isReactComponent(component) {
  return isClassComponent(component) || isFunctionComponent(component);
}

export const ApplyType = {
  NEW: '1', // 新开
  TRANSFER: '2', // 移机换号
  CHANGE: '3', // 换号
};

// 表单中下拉框用到的键值
export const renderField = {
  renderFieldNormal: {
    valueField: 'value',
    lableField: 'label',
  },
  renderFieldForAttr: {
    valueField: 'value',
    lableField: 'valueMark',
  },
  renderFieldForLinkedAttr: {
    valueField: 'attrValue',
    lableField: 'attrValueMark',
  },
  renderFieldForCertType: {
    valueField: 'certTypeId',
    lableField: 'certTypeName',
  },
  renderFieldForAddress: {
    valueField: 'addressId',
    lableField: 'addressName',
  },
  renderFieldForOccupation: {
    valueField: 'occupationId',
    lableField: 'occupationName',
  },
  renderFieldForBillingCycle: {
    valueField: 'billingCycleTypeId',
    lableField: 'billingCycleTypeName',
  },
  renderFieldForBillingMedia: {
    valueField: 'deliveryMethodId',
    lableField: 'deliveryMethodName',
  },
  renderFieldForReason: {
    valueField: 'orderReasonId',
    lableField: 'orderReasonName',
  },
  renderFieldForService: {
    valueField: 'serviceType',
    lableField: 'serviceTypeName',
  },
  renderFieldForSubService: {
    valueField: 'subServiceType',
    lableField: 'subServiceTypeName',
  },
  renderFieldForPaymentType: {
    valueField: 'paymentTypeId',
    lableField: 'paymentTypeName',
  },
  renderFieldForPaymentMethod: {
    valueField: 'paymentMethodId',
    lableField: 'paymentMethodName',
  },
  renderFieldForBank: {
    valueField: 'bankId',
    lableField: 'bankName',
  },
  renderFieldForBankAcctType: {
    valueField: 'accountType',
    lableField: 'accountTypeName',
  },
};

export const ADDR_DOWN = 'DOWN'; // 向上查询标识
export const ADDR_UP = 'UP'; // 向下查询标识

export const BSNL_ATTR = { // 属性
  GENDER: 'BSNL_GENDER',
  TITLE: 'BSNL_TITLE',
  MARITAL_STATUS: 'BSNL_MARITAL_STATUS',
  INCOME_GROUP: 'BSNL_INCOME_GROUP',
  ORGANISATION: 'BSNL_ORGANISATION',
  PURPOSE: 'BSNL_PURPOSE', // 对应页面的usage code
  ACCOUNT_TYPE: 'EXP_ACCOUNT_TYPE',
  ACCT_SUB_TYPE: 'EXP_ACCT_SUB_TYPE',
  ACQUISITION_TYPE: 'CPE_ACQUISITION_TYPE',
  BILL_TYPE: 'EXP_BILL_TYPE',
  CUSTOMER_CLASSIFICATION: 'BSNL_CUSTOMER_CLASSIFICATION',
  DO_NOT_CALL_REGISTRY: 'BSNL_DO_NOT_CALL_REGISTRY',
};

// 业务code
export const DEALER_EVENT_CODE = {
  NEW_CONNECTION: 'DSCM101', // 新装
  DISCONNECTION: 'DSCM201', // 拆机
  RECONNEECTION: 'DSCM202', // 重装
  CASUAL_EXTENSION: 'DSCM203', // 延长临时套餐有效期
  MODIFY_SCHEME_AND_PLAN: 'DSCM204', // 切换主套餐
  CHANGE_OF_INDICATOR_NORMAL: 'DSCM205', // 固话换号
  CHANGE_OF_INDICATOR_ISDN: 'DSCM206', // ISDN换号
  SHIFT_RELOCATION: 'DSCM207', // 移机
  MODIFYING_FIXED_LINE_NUMBER_GROUP: 'DSCM208', // 变更固话群组
  TRANSFER: 'DSCM209', // 过户
  VIRTUAL_CHANGE_OF_DESTINATION_NUMBER: 'DSCM210', // 变更呼叫转移号码
  VIRTUAL_CONVERT_TO_FIXED_LINE: 'DSCM211', // 虚拟号码变更为固话号码
  MODIFY_BROADBAND_NETWORK_IP: 'DSCM212', // 变更宽带网络IP
  MODIFY_BROADBAND_NETWORK_VPN: 'DSCM213', // 变更宽带网络VPN
  BHARAT_FIBER_AIR_FIBER_OTHERS_MODIFY: 'DSCM214', // Fiber业务其他变更
  MODIFY_FOR_SERVICE: 'DSCM215', // 客户资料修改
  MODIFY_VAS: 'DSCM216', // 变更VAS 修改附属套餐
  MODIFY_GOODS: 'DSCM217', // 变更实物
  ADD_SERVICES: 'DSCM218', // 加装
  MANAGE_EXTENSION: 'DSCM219', // 分机管理
};

// 操作类型
export const OPER_TYPE = {
  LOCK: 'LK', // 号码锁定
  UNLOCK: 'UL', // 号码解锁
};

// offer列表，默认展示条数
export const PLAN_DEFAULT_PAGE_SIZE = '5';

// serviceTyep映射 老版
// Landline: 26
// Broadband: 79
// Combo: 51
// ISDN: 83
// BB VPN: 902
// NGN(Wings): 905
// node Leased network: 71
// Leased port: 72
export const SERVICE_TYPE = {
  LANDLINE: '1',
  ISDN: '2',
  LL_POC: '3',
  BHARAT_FIBER: '4',
  BHARAT_AIR_FIBER: '5',
  BROADBAND: '6',
  IN: '7',
  NGN: '8',
  DSPT: '9',
  NIB: '10',
  VSAT: '11',
  VPN: '12',
  CUG: '13',
  LEASED_PORT: '14',
  NODE_LEASED_NETWORK: '14',
  IDC: '15',
  // BB_VPN: '902', // 未知， todo
  // ONT: '81', // 未知, todo
  // BB_VPN_NODE: '903', // 未知, todo
};

// serviceTyep映射 新版(现网)
// LANDLINE: 1
// ISDN: 2
// LL PCO: 3
// Bharat Fiber: 4
// BHARAT AIR FIBER: 5
// BROADBAND: 6
// IN: 7
// NGN Serivces: 8
// DSPT: 9
// NIB: 10
// VSAT: 11
// VPN: 12
// CUG: 13
// Lease line: 14
// IDC: 15

export const FORM_ITEM_NEW_VALUE = 'new';
export const FORM_ITEM_SAME_INS_ADDR = 'same';

export const renderPrice = price => {
  if (price && price.includes(defaultCurrencyUnit)) {
    return price;
  }
  return price ? `${defaultCurrencyUnit} ${price}` : '-';
};
export const DRM_VERIFY_CODE_EXPIRE_TIME = 'DRM_VERIFY_CODE_EXPIRE_TIME';
export const DRM_VERIFY_CODE_RESEND_TIME = 'DRM_VERIFY_CODE_RESEND_TIME';
export const DRM_DEFAULT_ACCNBR_PREFIX = 'DRM_DEFAULT_ACCNBR_PREFIX';
export const DRM_ETC_BALANCE_UNIT = 'DRM_ETC_BALANCE_UNIT';
export const DRM_DEF_CURRENCY_PRECISION = 'DRM_DEF_CURRENCY_PRECISION';
export const DRM_CURRENCY_SHOW_PRECISION = 'DRM_CURRENCY_SHOW_PRECISION';
export const DRM_APP_DOWNLOAD_URL = 'DRM_APP_DOWNLOAD_URL';
export const DRM_BSNL_POI_POA_RULE = 'DRM_BSNL_POI_POA_RULE';
export const DRM_BSNL_CAPTCHA_EXPIRE_CONFIG = 'DRM_BSNL_CAPTCHA_EXPIRE_CONFIG';
export const DRM_CLOUD_DISK_PATH = 'DRM_CLOUD_DISK_PATH';
export const ACCOUNTS_DASHBOARD_FILTER_LIST = 'ACCOUNTS_DASHBOARD_FILTER_LIST';

export const preferredMethodList = [
  { value: 'E', label: 'Email' },
  { value: 'M', label: 'Mobile' },
  { value: 'H', label: 'Home Phone' },
  { value: 'O', label: 'Work Phone' },
  // { value: 'F', label: 'Fax' }, // 页面没有收集，暂时注释 ---- 张楚楚
];

export const POA_EFF_DATE_FORMAT = 'YYYYMMDDHHmmss';
export const POI_EFF_DATE_FORMAT = 'YYYY/MM/DD';
export const COMOMN_PARAMS_DATE_FORMAT = 'YYYYMMDDHHmmss'; // 公共的时间格式入参，与sa确定接口统一用这个

export const AccessTypeCode = 'EXP_ACCESS_TYPE';

export const EXP_ACCESS_TYPE = {
  COPPER: 'COPPER', // 铜网
  FIBER: 'AIRFIBER', // 光网
};


// eslint-disable-next-line no-shadow
export function generateUploadProps({ limit = 1, getAlreadyUploadNum, dispatch, i18n = () => {} }, maxFileSize, fileTypeList) {
  if (dispatch && typeof dispatch !== 'function') {
    throw new Error(i18n('BUSINESS_DISPATCH_SHOULD_BE_FUNCTION'));
  }

  const currentUser = loginSession.getUser();
  const { _csrf } = currentUser;
  const prefix = getRequestPrefix();

  const fileRule = window.g_app.getStore().getState().business.DRM_BSNL_POI_POA_RULE;
  let fileRuleObj = {};
  try {
    fileRuleObj = JSON.parse(fileRule);
  } catch (e) {
    message.error('JSON parse error, please contact administrator');
    return false;
  }

  const size = maxFileSize || get(fileRuleObj, 'size') || 1024;
  // Note：压缩工具lrz不支持对.heic格式的支持，所以此次去掉image/heic ---- 2022/2/18
  const typeList = fileTypeList || (get(fileRuleObj, 'type') || 'image/png, image/jpeg, image/jpg, image/bmp, image/tiff, application/pdf').split(',');

  return {
    name: 'file',
    action: `${prefix}/ding/common/upload2dms`,
    headers: { [_csrf.headerName]: _csrf.token },
    accept: 'image/*, .doct, .docx, .pdf', // heic为苹果系统照片格式
    data: {},
    requiredRule: { size, typeList: typeList.map(type => type.substring(type.indexOf('/') + 1)) },
    onChange(info) {
      if (info.file.status === 'done') {
        if (info.file.response.code === '500') {
          message.error(info.file.response.resultMsg);
        } else {
          message.success(`${info.file.name} ${getBusinessI18n('UPLOAD_SUCCESS')}`);
        }
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} ${getBusinessI18n('UPLOAD_ERROR')}`);
      }
    },
    beforeUpload(file) {
      if (typeList.map(type => type.trim()).indexOf(file.type.toLowerCase()) === -1) {
        message.error(getBusinessI18n('CAN_UPLOAD_FILE_TYPE'));
        return false;
      }

      // 在此处判断下压缩后能否满足要求，能，则继续，在transformFile里对图片进行处理；不能，则停止上传
      // 压缩单号：2562007
      if (file.size / 1024 > size) { // 不能大于 1024KB，增加对图片的压缩，能压缩到1M内就上传，压缩不到1M内的就给出提示不让上传
        if (file.type.toLowerCase() !== 'application/pdf') {
          return lrz(file, { width: 1024 }).then(rst => {
            if (rst.file.size / 1024 > 1024) { // TODO 一直没有找到压缩后依然大于1M的图片，测试过12.6M的图片，压缩后依然只有66KB，所以对于一般情况，压缩后大于1M的应该会比较少
              message.error(getBusinessI18n('MAX_UPLOAD_FILE_SIZE'));
              return Promise.reject();
            }
            return Promise.resolve();
          });
        }
        message.error(getBusinessI18n('MAX_UPLOAD_FILE_SIZE'));
        return false;
      }
      if (typeof getAlreadyUploadNum === 'function' && getAlreadyUploadNum() >= limit) {
        message.error(`${getBusinessI18n('ONLY_ALLOW')} ${limit} ${getBusinessI18n('FILE')}`);
        return false;
      }
      return true;
    },
    transformFile(file) {
      if (file.type.toLowerCase() !== 'application/pdf') {
        // lrz使用文档：https://github.com/think2011/localResizeIMG/wiki/2.-%E5%8F%82%E6%95%B0%E6%96%87%E6%A1%A3
        // 大于1024KB的图片进行压缩
        return lrz(file, { width: 1024 }).then(rst => Promise.resolve(rst.file));
      }
      return file;
    },
    onDownload(e) { // TODO bug pending for fix：点击一次下载两遍
      // eslint-disable-next-line no-shadow
      const docId = get(e, 'attachUrl') || get(e, 'response.data.docId');
      const docName = get(e, 'name');
      dispatch({
        type: 'business/downloadFile',
        payload: { docId, docName },
      });
    },
  };
}

export const INDIVIDUAL_CUT_TYPE = 'A';

// 根据addressId从addrList找到对应的地址并返回地址名称
export function findAddressName(addrList, addressId) {
  return get(addrList.find(addr => addr.addressId === addressId), 'addressName');
}
// 获取订单状态列表
export const getOrderStateList = (i18nT = i18n) => [
  { value: 'I', label: i18nT('ORDER_ORDER_INTAKE'), color: '#FFA034' }, // 录入Order Intake
  { value: 'D', label: i18nT('ORDER_ORDER_DECOMPOSITION'), color: '#FFA034' }, // 订单分解Order Decomposition
  { value: 'F', label: i18nT('ORDER_FEASIBILITY_CHECK'), color: '#FFA034' }, // 审核Feasibility Check
  { value: 'P', label: i18nT('ORDER_PROVISIONING_ACTIVATION'), color: '#FFA034' }, // 服务开通/激活Provisioning/Activation
  { value: 'C', label: i18nT('ORDER_COMPLETION'), color: '#2CD487' }, // 竣工Completion
  { value: 'E', label: i18nT('ORDER_CANCEL'), color: '#2CD487' }, // 撤销Cancel
  { value: 'B', label: i18nT('ORDER_WAIT'), color: '#FFA034' }, // 待缴费
  { value: 'S', label: i18nT('ORDER_SUSPEND'), color: '#FFA034' }, // 暂停
  { value: 'G', label: i18nT('ORDER_EXCEPTION'), color: '#FFA034' }, // 异常
  { value: 'M', label: i18nT('ORDER_REVISION_CANCELLING'), color: '#FFA034' }, // 修正并撤销
  { value: 'A', label: i18nT('ORDER_CANCELLING'), color: '#FFA034' }, // 撤单中
  { value: 'H', label: i18nT('ORDER_POSTDATING'), color: '#FFA034' }, // ??
  { value: 'J', label: i18nT('ORDER_PROVISIONING_ERROR'), color: '#FFA034' }, // 订单开通异常
  { value: 'K', label: i18nT('ORDER_WAITING_PROVISIONING'), color: '#FFA034' }, // 等待施工
  { value: 'N', label: i18nT('ORDER_PENDING_CUSTOMER_ORDER'), color: '#FFA034' }, // pending Customer Order
  { value: 'L', label: i18nT('ORDER_UPDATE_BILLING'), color: '#FFA034' }, // 资料开通可计费，但订单未竣工
  { value: 'R', label: i18nT('ORDER_RETAINING'), color: '#FFA034' }, // 拆机挽留中Retaining
  { value: 'U', label: i18nT('ORDER_NOT_FEASIBLE'), color: '#FFA034' }, // Feasibility Failed
  { value: 'V', label: i18nT('ORDER_WAITING_FOR_PAYMENT'), color: '#FFA034' }, // Waiting for Payment
  { value: 'W', label: i18nT('ORDER_ASSIGNED_TO_CO'), color: '#FFA034' }, // Assigned to CO
  { value: 'X', label: i18nT('ORDER_APPROVED_BY_CO'), color: '#FFA034' }, // Approved by CO
  { value: 'Y', label: i18nT('ORDER_DEMAND_NOTE_ASSIGNED_TO_AOTR'), color: '#FFA034' }, // Demand Note Assigned to AOTR
  { value: 'Q', label: i18nT('ORDER_RETURNED_BY_CO'), color: '#FFA034' }, // Returned by CO
  { value: 'Z', label: i18nT('ORDER_FEASIBLE'), color: '#FFA034' }, // Feasible
  { value: '1', label: i18nT('ORDER_FEASIBLE_BY_CO'), color: '#FFA034' }, // Feasible by CO
  { value: 'O', label: i18nT('ORDER_WAITLISTED'), color: '#FFA034' }, // Reject and wait to reopen
  { value: 'T', label: i18nT('ORDER_WAIT_ARCHIVE'), color: '#FFA034' }, // 同一客户订单下的其他订单送O,当前订单没送
];

// 适用于只有一层结构的对象，复杂结构对象请使用
export function emptyObj(obj) {
  const newObj = {};
  Object.keys(obj).forEach(key => {
    const keyType = Object.prototype.toString.call(obj[key]);
    if (keyType === '[object Array]') {
      newObj[key] = [];
    } else if (keyType === '[object String]') {
      newObj[key] = '';
    } else if (keyType === '[object Object]') {
      newObj[key] = {};
    } else if (keyType === '[object Number]') {
      newObj[key] = 0;
    }
  });
  return newObj;
}

export const AVAILABLE = {
  YES: 'Y',
  NO: 'N',
};

// 获取套餐协议时间
// I  Intraday
// H  Hour
// A  Intramonthly
// Y  Year
// M  Month
// W  Week
// D  Day
// S  Exact Time(For Once)
// C  Billing Cycle
export const getPlanTimeUnitList = (i18nT = i18n) => [
  { value: 'I', label: i18nT('PLAN_INTRADAY') },
  { value: 'H', label: i18nT('PLAN_HOUR') },
  { value: 'A', label: i18nT('PLAN_INTRAMONTHLY') },
  { value: 'Y', label: i18nT('PLAN_YEAR') },
  { value: 'M', label: i18nT('PLAN_MONTH') },
  { value: 'W', label: i18nT('PLAN_WEEK') },
  { value: 'D', label: i18nT('PLAN_DAY') },
  { value: 'S', label: i18nT('PLAN_EXACT_TIME_FOR_ONCE') },
  { value: 'C', label: i18nT('PLAN_BILLING_CYCLE') },
];

// 'A', 'B', 'C', 'H', 'I', 'J'
// J Error：流程配置不对或者代码异常创建TT单的时候会报错Error，这种异常的TT单创建时候就报错了不会再向下流转了。（#FFA034）【黄色：告警】
// B Submit：TT单提交状态；（#4477EE）【蓝色：处理中的】
// C Close：TT单被关闭；（#909090）【灰色：单子非正常结束】
// H Finish：就是整个TT单子结束了（#2CD487）【绿色：单子正常结束】
// I Resolved：O侧通知TTO归档后，状态为Resolved （#4477EE）【蓝色：处理中的】
// A Draft：创建的草稿状态的TT单，不起流程。（#909090）【灰色：单子非正常结束
export const finishStateOption = ['A', 'C', 'H'];
const stateColorOption = {
  J: '#FFA034',
  B: '#4477EE',
  C: '#909090',
  H: '#2CD487',
  I: '#4477EE',
  A: '#909090',
};
export const getStateColor = state => stateColorOption[state];

export function getBillingAccountInfoCompProps(state, namespace) {
  return {
    stateAddrOpts: state[namespace].stateAddrOpts || [],
    districtOpts: state[namespace].districtOpts || [],
    mainLocalityOpts: state[namespace].mainLocalityOpts || [],
    subLocalityOpts: state[namespace].subLocalityOpts || [],
    attrObj: state[namespace].attrObj || {},
    deliveryMethodList: state[namespace].deliveryMethodList || [],
    billingCycleTypeList: state[namespace].billingCycleTypeList || [],
    acctDtoList: state[namespace].acctDtoList || [],
    acctDetail: state[namespace].acctDetail || [],
    accountInfo: state[namespace].accountInfo || {},
    custAddrList: state[namespace].custAddrList || [],
    custAddressId: state[namespace].custAddressId,
    POACertTypeList: state[namespace].POACertTypeList || [],
    isCreate: state[namespace].isCreate,
    searchCustInfo: state[namespace].searchCustInfo || {},
    custValueMap: state[namespace].custValueMap || {},
    custCertValue: state[namespace].custCertValue || {},
    custType: state[namespace].custType || 'A',
    custId: state[namespace].custId,
    poaDefaultFileList: state[namespace].poaDefaultFileList || [],
    timing: state.business.timing,
    times: state.business.times,
    sendTime: state.business.sendTime,
    dateFormat: state.dxpGlobal.sysparams.dateFormat,
    expiryTime: state.business.DRM_VERIFY_CODE_EXPIRE_TIME,
    timeLoading: (state.loading.models.business && !state.loading.effects['business/timing']) || false,
    loading: (state.loading.models.business && !state.loading.effects['business/timing']) || false,
    subServiceType: get(state[namespace], 'subServiceType.subServiceType'),
  };
}

// 设备采集对应的信息
// CPE ACQUISITION TYPE CPE_ACQUISITION_TYPE
// DEVICE RECEIPT NUMBER --  BSNL_DEVICE_RECEIPT_NUMBER
// DEVICE FRANCHISEE  -- BSNL_DEVICE_FRANCHISEE_CODE
// DEVICE RENTAL TYPE -- BSNL_DEVICE_RENTAL_TYPE
export const AcqAttrCodes = {
  CPE_ACQUISITION_TYPE: 'CPE_ACQUISITION_TYPE',
  RECEIPT_NUMBER: 'BSNL_DEVICE_RECEIPT_NUMBER',
  FRANCHISEE: 'BSNL_DEVICE_FRANCHISEE_CODE',
  RENTAL_TYPE: 'BSNL_DEVICE_RENTAL_TYPE',
  REQUIRED: 'BSNL_INSTALLATION_REQUIRED',
};

export const AcqAttrCodesLabelKeys = {
  [AcqAttrCodes.REQUIRED]: 'OFFER_INSTALLATION_REQUIRED',
  [AcqAttrCodes.CPE_ACQUISITION_TYPE]: 'SELECT_OFFER_ACQUISITION_TYPE',
  [AcqAttrCodes.RECEIPT_NUMBER]: 'RELATEED_OFFER_DEVICE_RECEIPT_NO',
  [AcqAttrCodes.FRANCHISEE]: 'RELATEED_OFFER_DEVICE_FRANCHISEE',
  [AcqAttrCodes.RENTAL_TYPE]: 'RELATEED_OFFER_DEVICE_RENTAL_TYPE',
};

export const getConfirmDeleteTitleI18n = (i18nT, path) => i18nT('COMMON_CONFIRM_DELETE_TITLE', { item: path });
export const getMessageNoChangeI18n = (i18nT, path) => i18nT('COMMON_MESSAGE_NO_CHANGE', { item: path });

export const maxUploadFileNums = 999;

// vas操作 A：新增 M：修改 D：删除
export const VAS_ACT_TYPE_MAP = {
  ADD: 'A',
  MODIFY: 'M',
  DELETE: 'D',
};

export const AccessType = { // ISDN AccessType
  PRI: 'PRI',
  BRI: 'BRI',
};

export const NbrOperType = {
  LOCKED: 'LK', // 号码锁定
  UN_LOCKED: 'UL', // 号码解锁
};

export const ADDRESS_NAME = {
  STATE: 'stateAddrOpts',
  CITY: 'districtOpts',
  MAIN_LOCALITY: 'mainLocalityOpts',
  SUB_LOCALITY: 'subLocalityOpts',
};

// 业务的subsEventId crm事件标识
export const SUBS_EVENT_ID = {
  modifyPlan: '329',
  reconnection: '178',
  newConnection: '1',
  addServices: '544',
  updateBillingAddress: '10225',
  updateBillingFrequency: '10207',
  updateBillingDeliveryType: '10226',
  updateBillStatementType: '10227',
  updateMethodOfPayment: '10209',
  shift: '10008',
  transfer: '67',
  transferWithShift: '10011',
  changeMainProduct: '35',
  modifyGoods: '189',
};

// 查询客户地址列表接口qryCustAddrList 新增入参
export const ADDRESS_TYPE_FLAG = {
  ALL_ADDR: 0, // 默认地址，既是安装地址又是账户地址
  INSTALLATION_ADDR: 1, // 安装地址
  BILLING_ADDR: 2, // 账户地址
};

export const PAYMENT_METHOD = {
  ECS: '103',
  NACH: '104',
  CREDIT_CARD: '18',
};

export const PAYMENT_TYPE = {
  AUTO: 'A',
  MANUAL: 'B',
};

export const SelectOfferObj = {
  number: {},
  offerDetail: {},
  attrDetail: {},
};

export const DefaultJudeOpt = [
  {
    value: 'N',
    label: 'NO',
  },
  {
    value: 'Y',
    label: 'YES',
  },
];

export const needCheckAcctModels = ['shift', 'transferCustomer'];

export function needToQryBankAcctType(paymentMethod) {
  return [PAYMENT_METHOD.CREDIT_CARD, PAYMENT_METHOD.ECS, PAYMENT_METHOD.NACH].includes(paymentMethod);
}

export const BankCardType = {
  MASTERCard: '4',
  DinersClub: '5',
  AmericanExpress: '6',
  Discover: '7',
  Visa: '8',
};

// 仅用于获取poa的附件列表
export function getDefaultFileList(poaDefaultFileList = [], selectedAddrId) {
  if (FORM_ITEM_NEW_VALUE === selectedAddrId) {
    return [];
  }
  return (poaDefaultFileList || []).find(fileList => get(fileList, 'length') > 0 ? String(get(fileList, '0.custAddressId')) === String(selectedAddrId) : false) || poaDefaultFileList[0] || [];
}

// 地址name拼接见单子 2758284
export function getAddrName(addr) {
  return `${get(addr, 'address2', '')},${get(addr, 'addrExtMap.village', '')},${get(addr, 'addrExtMap.subLocalityName', '')},${get(addr, 'addrExtMap.mainLocalityName', '')},${get(addr, 'addrExtMap.districtName', '')},${get(addr, 'addrExtMap.stateName', '')},${get(addr, 'addrExtMap.country', '')}`;
}

export function getOrderAddrName(addr) {
  return `${get(addr, 'houseNo', '')},${get(addr, 'road', '')},${get(addr, 'area', '')},${get(addr, 'village', '')},${get(addr, 'subLocalityName', '')},${get(addr, 'mainLocalityName', '')},${get(addr, 'districtName', '')},${get(addr, 'state', '')}`;
}
// A-add,D-delete;M-modify
export const ActTypeObj = {
  ADD: 'A',
  DELETE: 'D',
  MODIFY: 'M',
};

export function compareDocList(docList1 = [], docList2 = []) { // 比较两个附件列表是否一致，一致返回true，否则返回false
  if (get(docList1, 'length') !== get(docList2, 'length')) {
    return false;
  }

  return !docList1.some((doc, index) => get(doc, 'attachUrl') !== get(docList2, `${index}.docId`));
}

export function comparePoaDtoAttachList(attch1 = [], attach2 = []) { // 比较两个附件列表是否一致，一致返回true，否则返回false
  if (get(attch1, 'length') !== get(attach2, 'length')) {
    return false;
  }

  return !attch1.some((doc, index) => get(doc, 'attachUrl') !== get(attach2, `${index}.attachUrl`));
}

export function custChange(updateCustParams, custValueMapCopy) {
  return updateCustParams.firstName !== custValueMapCopy.firstName || updateCustParams.middleName !== custValueMapCopy.secondName
  || updateCustParams.lastName !== custValueMapCopy.thirdName
  || get(updateCustParams.custAttrDtoList.find(attr => attr.attrCode === 'BSNL_DO_NOT_CALL_REGISTRY'), 'attrValue') !== get(custValueMapCopy, 'BSNL_DO_NOT_CALL_REGISTRY.attrValue')
  || get(updateCustParams.custAttrDtoList.find(attr => attr.attrCode === 'BSNL_CUSTOMER_CLASSIFICATION'), 'attrValue') !== get(custValueMapCopy, 'BSNL_CUSTOMER_CLASSIFICATION.attrValue');
}

// 比较两个poa是否有差异
export function poaChange(poa1, poa2) {
  return get(poa1, 'certType') !== get(poa2, 'certType') ||
  get(poa1, 'certNbr') !== get(poa2, 'certNbr') ||
  get(poa1, 'issueOrg') !== get(poa2, 'issueOrg') ||
  get(poa1, 'poaEffDate') !== get(poa2, 'poaEffDate') ||
  get(poa1, 'poaExpDate') !== get(poa2, 'poaExpDate') ||
  get(poa1, 'attachDtoList.length') !== get(poa2, 'attachDtoList.length') ||
  !comparePoaDtoAttachList(get(poa1, 'attachDtoList'), get(poa2, 'attachDtoList'));
}

// 比较两个poi是否有差异
export function poiChange(poi, poiCopy) {
  return get(poi, 'certType') !== get(poiCopy, 'certTypeId') ||
  get(poi, 'certNbr') !== get(poiCopy, 'certNbr') ||
  get(poi, 'issueOrg') !== get(poiCopy, 'issueOrg') ||
  get(poi, 'poiEffDate') !== get(poiCopy, 'poiEffDate') ||
  get(poi, 'poiExpDate') !== get(poiCopy, 'poiExpDate') ||
  get(poi, 'attachDtoList.length') !== get(poiCopy, 'docDtoList.length') ||
  !compareDocList(get(poi, 'attachDtoList'), get(poiCopy, 'docDtoList'));
}

export function tokenCountDown() {
  /* eslint-disable no-underscore-dangle */
  window.g_app._store
    .dispatch({
      type: 'bsnlLogin/getSessionTime',
    })
    .then(data => {
      delete window.openFlag;
      clearInterval(window.timeoutExit);
      const interval = Number(data.interval) || 30;
      const noticeTime = Number(data.noticeTime) || 100;
      window.openFlag = true;
      window.timeoutExit = setInterval(() => {
        window.g_app._store
          .dispatch({
            type: 'bsnlLogin/getSessionRemineTime',
          })
          .then(remineTime => {
            let routePath = window.location.hash.includes('/') ? window.location.hash.split('/')[1] : '';
            routePath = routePath.includes('?') ? routePath.split('?')[0] : routePath;
            if (routePath === 'userLogin') return;

            if (remineTime <= noticeTime && window.openFlag) {
              window.openFlag = false;
              // 弹出对话框提醒
              let secondsToGo = remineTime;
              const modal = Modal.warning({
                width: 650,
                title: i18n('SYSTEM_AUTO_LOGOUT'),
                content: `${i18n('SYSTEM_SESSION_EXPIRE_NOTICE')} ${secondsToGo} ${i18n('SECONDS')}.`,
                okText: i18n('SYSTEM_SESSION_CONTINUE'),
                onOk: () => {
                  window.g_app._store
                    .dispatch({
                      type: 'bsnlLogin/refreshSessionTime',
                    })
                    .then(() => {
                      window.openFlag = true;
                      clearInterval(window.countdown);
                      clearTimeout(window.exit);
                    });
                },
              });
              window.countdown = setInterval(() => {
                secondsToGo -= 1;
                modal.update({
                  content: `${i18n('SYSTEM_SESSION_EXPIRE_NOTICE')} ${secondsToGo} ${i18n('SECONDS')}.`,
                });
              }, 1000);
              window.exit = setTimeout(() => {
                modal.destroy();
                delete window.openFlag;
                clearInterval(window.timeoutExit);
                clearInterval(window.countdown);
                clearTimeout(window.exit);
                // drm侧不需要进入登陆页,portal侧进入登录页
                // window.g_app._store.dispatch({
                //   type: 'bsnlLogin/logout',
                // });
                window.parent.portal.appGlobal.set('currentStatus', 'sessionTimeOut');
              }, secondsToGo * 1000);
            }
          });
      }, interval * 1000);
    });
}
